// ============================================================================
// Where a tapped push alert goes, decided by the page (RIZ132, Task 11).
//
// An iPhone's Home Screen app ignores the url a tap asks for (live acceptance,
// iOS 18.7): with the app in the background the worker's navigate() on its
// window is refused or ignored, so the app resumes on the page it was showing,
// and with the app closed openWindow(url) opens it at its start URL (WebKit bug
// 263687). Every alert arrived; no tap opened its page.
//
// So public/push-sw.js no longer moves a window. On a tap it leaves a NOTE in
// Cache Storage ({ url, at } at TAP_NOTE_KEY in TAP_NOTE_CACHE), then focuses
// the Rize OS window it controls and posts it { type: PUSH_OPEN, url }, or opens
// a new window. The page routes itself (createTapRouter, below, which
// src/hooks/usePushTapRouting.ts runs):
//   - a message is routed at once, before Cache Storage is touched;
//   - the note is read on mount and whenever the page comes back on screen, and
//     again 0.5, 1.5 and 3 s after each, because an iPhone can bring the app
//     forward before the worker has written the note;
//   - every wait on Cache Storage gives up after TAP_NOTE_WAIT_MS, so one that
//     never answers holds nothing up.
// The bell's ?n= arrival then marks the row read, as before.
//
// This file is the part of that which needs no browser, so node --test drives it
// (tests/unit/riz132-push-tap.test.mjs): the rules, and the router, which is
// handed the clock, the timers and Cache Storage instead of reaching for them.
// NOTHING IS IMPORTED HERE, ON PURPOSE, the pushSupport.ts idiom: a module that
// reached for React, the router or the `@/` alias could not be loaded by
// `node --test` at all.
// ============================================================================

/** The message type a tap posts to the window it brings forward. public/push-sw.js repeats it (pinned equal). */
export const PUSH_OPEN = 'rizeos:push-open'

/** The Cache Storage cache and key the worker leaves a tap's note at. public/push-sw.js repeats both (pinned equal). */
export const TAP_NOTE_CACHE = 'rizeos-push'
export const TAP_NOTE_KEY = '/__rizeos/pending-tap'

/**
 * How long a note counts as a tap still on its way. A cold start of the Home Screen app, the session check
 * and the first render take seconds; a note older than this is a tap the person has long since stopped
 * waiting on, and routing to it now would yank them off whatever they opened instead.
 */
export const TAP_NOTE_MAX_AGE_MS = 120_000

/**
 * The longest the page waits on Cache Storage (a read or a delete) before it carries on without it: the same cap
 * as the worker's NOTE_WAIT_MS for writing the note (pinned equal). A Cache Storage that never answers then holds
 * up no tap and blocks no later one.
 */
export const TAP_NOTE_WAIT_MS = 500

/**
 * When a check reads the note again, in ms after it first read it. An iPhone can bring the app forward before the
 * worker has written the note (the tap and the coming forward race), so the visibility check alone can find
 * nothing, and nothing else would read the note that lands a moment later; three more reads over three seconds
 * catch it. Not a poll: the reads stop at 3 s, and a new check starts them again.
 */
export const TAP_NOTE_REREAD_MS: readonly number[] = Object.freeze([500, 1_500, 3_000])

/** A tap's note as the worker leaves it: the page, and the time of the tap (Date.now(), the device's clock). */
export interface TapNote {
  url: string
  at: number
}

/** The tap the page last routed to, or found already open, and when (the page's Date.now()). */
export interface SettledTap {
  url: string
  at: number
}

/** Where the page is: its address now and the address its layout first rendered at (pathname + search, no fragment). */
export interface PageAddresses {
  current: string
  landed: string
}

// The site as push-rules.ts and the worker name it: a name that can never resolve (RFC 2606).
const SITE_BASE = 'https://rize.invalid'

/**
 * `url` when it is a path on this site as safeSitePath (supabase/functions/_shared/push-rules.ts) and the
 * worker's safePath write one, else null: one leading slash and never two, no backslash, no control character
 * anywhere, and nothing the URL parser would read as another host or scheme. Never looser than the server.
 * Stricter in two ways, neither of which a producer's link meets:
 *   - a backslash anywhere is refused, where the server keeps one that sits in the query or the fragment (the
 *     parser only reads a backslash as a slash in the path, which is where it could name another host);
 *   - the url must already be the parser's own spelling (dot segments resolved, spaces and non-ASCII
 *     percent-encoded), which everything the worker writes is.
 * Both pinned by the test over the shared hostile lists and a seeded corpus.
 */
export function sitePath(url: unknown): string | null {
  if (typeof url !== 'string' || !url.startsWith('/') || url.startsWith('//') || url.includes('\\') || /\p{Cc}/u.test(url)) return null
  try {
    const parsed = new URL(url, SITE_BASE)
    if (parsed.protocol !== 'https:' || parsed.origin !== SITE_BASE) return null
    return parsed.pathname + parsed.search + parsed.hash === url ? url : null
  } catch {
    return null
  }
}

/** Whether `at` is within TAP_NOTE_MAX_AGE_MS of `now`, either side. */
function isRecent(at: number, now: number): boolean {
  return Number.isFinite(at) && Math.abs(now - at) < TAP_NOTE_MAX_AGE_MS
}

/**
 * A note read from Cache Storage as { url, at }, when it names a page of this site and was left within
 * TAP_NOTE_MAX_AGE_MS of `now`; null for anything else (stale, broken, another site). A note dated ahead of the
 * page is believed for as long as one behind it and no longer: the worker and the page read the same clock, so
 * only a clock put back after the tap dates a note ahead.
 */
export function freshNote(value: unknown, now: number): TapNote | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null
  const { url, at } = value as { url?: unknown; at?: unknown }
  const path = sitePath(url)
  if (path === null || typeof at !== 'number' || !isRecent(at, now)) return null
  return { url: path, at }
}

/** The url a push-open message names; null for any other message on the service-worker channel (the ping's answer, Workbox's own) or a url that is not a page of this site. */
export function pushOpenUrl(data: unknown): string | null {
  if (typeof data !== 'object' || data === null || (data as { type?: unknown }).type !== PUSH_OPEN) return null
  return sitePath((data as { url?: unknown }).url)
}

/** A site path without its fragment: pathname + search, what an address is compared on. */
function withoutFragment(path: string): string {
  const at = path.indexOf('#')
  return at === -1 ? path : path.slice(0, at)
}

/**
 * Whether a tap's page is already open, so that routing to it would only push a second copy (and mark its row
 * twice): the page's address now is the tap's page, or the address this layout first rendered at is. The
 * second is the page the tap itself opened: Chromium's openWindow honours the url, and by the time the note is
 * read the bell has already dropped ?n= from the address. An iPhone opens the app at its start URL, which is
 * not the tap's page. The fragment does not make another page; the path and the query do.
 */
export function alreadyOpen(target: string, here: PageAddresses): boolean {
  const page = withoutFragment(target)
  return page === withoutFragment(here.current) || page === withoutFragment(here.landed)
}

/**
 * Whether a note just read is to be deleted. A check uses every note once: a fresh one is routed, a stale or broken
 * one is cleared. After a message, the note goes when it is that tap's own, or stale, or broken; a fresh note for
 * another page is a later tap's, still on its way, and stays.
 */
export function dropsNote(message: string | null, note: { value: unknown } | null, now: number): boolean {
  if (note === null) return false
  if (message === null) return true
  const fresh = freshNote(note.value, now)
  return fresh === null || fresh.url === message
}

/** What the page does about a tap: delete the note or not, route or not, and which tap is now settled. */
export interface TapPlan {
  /** Delete the note (dropsNote). */
  dropNote: boolean
  /** The page to route to (a push), or null. */
  navigateTo: string | null
  /** The tap this decision settled (routed now, or found already open or already routed), for the page to remember; null when there was none. */
  settled: string | null
}

/**
 * One decision for both ways a tap reaches the page.
 *   - `message`: the url a push-open message named, or null when the page is checking for a note on its own
 *     (on mount, or on coming back on screen).
 *   - `note`: what the cache held ({ value } as parsed), or null when it held nothing or could not be read.
 *   - `settled`: the tap the page last settled, so a message and a note for one tap route it once, whichever
 *     comes first (on an iPhone they arrive together as the app comes forward; on Chromium a hidden window's
 *     visibility change and the message do).
 * A tap is routed unless it was settled within TAP_NOTE_MAX_AGE_MS or its page is already open.
 */
export function planTap(input: {
  message: string | null
  note: { value: unknown } | null
  now: number
  here: PageAddresses
  settled: SettledTap | null
}): TapPlan {
  const { message, note, now, here, settled } = input
  const fresh = note === null ? null : freshNote(note.value, now)
  const dropNote = dropsNote(message, note, now)
  const target = message ?? fresh?.url ?? null
  if (target === null) return { dropNote, navigateTo: null, settled: null }
  const handled = settled !== null && settled.url === target && isRecent(settled.at, now)
  return { dropNote, navigateTo: handled || alreadyOpen(target, here) ? null : target, settled: target }
}

// ── The router: what usePushTapRouting does, with everything it touches handed to it ───────────────────────────

/** The part of Cache Storage the page uses. window.caches is one; a test passes its own. */
export interface NoteStorage {
  match(request: string, options: { cacheName: string }): Promise<{ text(): Promise<string> } | undefined>
  open(cacheName: string): Promise<{ delete(request: string): Promise<boolean> }>
}

/** setTimeout and clearTimeout, or a test's own clock. */
export interface TapTimers {
  set(run: () => void, ms: number): ReturnType<typeof setTimeout>
  clear(handle: ReturnType<typeof setTimeout>): void
}

/** What the router is handed. */
export interface TapRouterDeps {
  /** Cache Storage, or null where the page has none (a message still routes; a check finds nothing). */
  storage: NoteStorage | null
  timers: TapTimers
  /** The page's clock (Date.now). */
  now: () => number
  /** Where the page is, asked at each decision. */
  here: () => PageAddresses
  /** Moves the page to a site path, as a push. */
  route: (url: string) => void
  /** The tap last settled, shared by every router the page makes (one a mount), so a remount does not route it again. */
  memory: { settled: SettledTap | null }
  /** Where a failure is said. The router never throws. */
  log: { warn: (...args: unknown[]) => void; error: (...args: unknown[]) => void }
}

/** The router a mount of usePushTapRouting runs. */
export interface TapRouter {
  /** A push-open message named `url`: route it at once, then clear its note. Resolves once the note is dealt with. */
  message(url: string): Promise<void>
  /** Read the note now, and again at each TAP_NOTE_REREAD_MS (on mount, and when the page comes back on screen). */
  check(): void
  /** The layout went away: clear the re-reads, and route on no check's read still in flight (its note stays for the next mount). A message's tidy-up may still delete its own tap's note: that tap was routed before the unmount. */
  stop(): void
}

/**
 * Routes a tapped alert's page from a message or the worker's note (see the file's header for why the page does
 * this itself). One tap is routed once, however many of the message, the check and the re-reads reach the page
 * (planTap, and `memory`), and a page already open is not pushed again. A page is routed before its note is
 * deleted, and every wait on Cache Storage gives up after TAP_NOTE_WAIT_MS, so nothing holds a tap up.
 */
export function createTapRouter(deps: TapRouterDeps): TapRouter {
  let live = true
  let rereads: ReturnType<typeof setTimeout>[] = []

  // `work`, or a rejection once TAP_NOTE_WAIT_MS has passed without an answer.
  const waitAtMost = <T>(work: Promise<T>, what: string): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const timer = deps.timers.set(() => {
        reject(new Error(`Cache Storage did not answer within ${TAP_NOTE_WAIT_MS} ms (${what})`))
      }, TAP_NOTE_WAIT_MS)
      work.then(
        (value) => {
          deps.timers.clear(timer)
          resolve(value)
        },
        (err: unknown) => {
          deps.timers.clear(timer)
          reject(err)
        },
      )
    })

  // A note's text as JSON, or null when it does not parse (a broken note, which is then cleared).
  const parse = (text: string): unknown => {
    try {
      return JSON.parse(text)
    } catch (err) {
      deps.log.warn('usePushTapRouting: the tapped alert\'s note is not JSON; clearing it:', err)
      return null
    }
  }

  // The note, as { value }, or null: none, no Cache Storage, or a read that failed or did not answer (said).
  // CacheStorage.match with a cacheName reads without creating the cache.
  const readNote = async (): Promise<{ value: unknown } | null> => {
    const storage = deps.storage
    if (!storage) return null
    try {
      const response = await waitAtMost(storage.match(TAP_NOTE_KEY, { cacheName: TAP_NOTE_CACHE }), 'reading the note')
      if (!response) return null
      return { value: parse(await waitAtMost(response.text(), 'reading the note')) }
    } catch (err) {
      deps.log.warn('usePushTapRouting: could not read the tapped alert\'s note:', err)
      return null
    }
  }

  // Deletes the note. A delete that fails or does not answer is said; the tap is settled either way, so a note
  // left behind is never routed twice.
  const dropNote = async (): Promise<void> => {
    const storage = deps.storage
    if (!storage) return
    try {
      await waitAtMost(storage.open(TAP_NOTE_CACHE).then((cache) => cache.delete(TAP_NOTE_KEY)), 'deleting the note')
    } catch (err) {
      deps.log.warn('usePushTapRouting: could not delete the tapped alert\'s note:', err)
    }
  }

  // Decides what the page knows now and routes at once, before any wait. Returns the decision.
  const decide = (message: string | null, note: { value: unknown } | null): TapPlan => {
    const now = deps.now()
    const plan = planTap({ message, note, now, here: deps.here(), settled: deps.memory.settled })
    if (plan.settled !== null) deps.memory.settled = { url: plan.settled, at: now }
    if (plan.navigateTo !== null) deps.route(plan.navigateTo)
    return plan
  }

  // One read of the note: route it if it is fresh and not yet settled, then delete it.
  const read = async (): Promise<void> => {
    try {
      const note = await readNote()
      if (!live || note === null) return
      if (decide(null, note).dropNote) await dropNote()
    } catch (err) {
      deps.log.error('usePushTapRouting: could not open the tapped alert\'s page:', err)
    }
  }

  const stopRereads = (): void => {
    for (const timer of rereads) deps.timers.clear(timer)
    rereads = []
  }

  return {
    async message(url) {
      if (!live) return
      try {
        // the message names the page: route now, then deal with the note
        decide(url, null)
        const note = await readNote()
        // only whether to delete it: this tap was routed above and is never routed again here
        if (dropsNote(url, note, deps.now())) await dropNote()
      } catch (err) {
        deps.log.error('usePushTapRouting: could not open the tapped alert\'s page:', err)
      }
    },
    check() {
      if (!live) return
      stopRereads()
      void read()
      rereads = TAP_NOTE_REREAD_MS.map((ms) => deps.timers.set(() => {
        void read()
      }, ms))
    },
    stop() {
      live = false
      stopRereads()
    },
  }
}
